import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { openModal } from './modal_controller';

// The shared "message box" modal - the classic pattern from native UI toolkits
// (Win32 MessageBox, Cocoa NSAlert, Qt QMessageBox): one dialog, parameterized by
// which buttons it shows, instead of a separate modal per use case.
//
//   showMessage('Export created successfully.')        // OK only, fire-and-forget,
//                                                        // replaces window.alert()
//   const ok = await confirmMessage('Delete this?');    // Cancel/Confirm, returns
//   if (!ok) return;                                    // Promise<boolean>, replaces
//                                                        // window.confirm()
//
// The message is inserted via innerHTML, so it may contain trusted, server-rendered
// markup (e.g. a link built with `link_to` in Rails and passed through a Stimulus
// value) - never interpolate raw user input into it.
export function showMessage(message) {
  window.__prospectMessage?.showNotice(message);
}

export function confirmMessage(message) {
  const controller = window.__prospectMessage;
  if (!controller) {
    // the shared modal isn't on the page (e.g. a layout without it) - fall back to
    // the native dialog rather than gating nothing
    return Promise.resolve(window.confirm(message));
  }
  return controller.askConfirm(message);
}

// exposed globally so `on_click: "showMessage(...)"`/`on_click: "confirmMessage(...)"`
// work from inline HAML button handlers, which can't reach these via an ES module import
window.showMessage = showMessage;
window.confirmMessage = confirmMessage;

// Turbo calls this with (message, formElement, submitter) and awaits the result -
// see https://turbo.hotwired.dev/reference/drive#confirmation. We only need the message.
Turbo.config.forms.confirm = (message) => confirmMessage(message);

export default class extends Controller {
  static targets = ['message', 'cancelButton', 'confirmButton'];
  static values = { okLabel: String, confirmLabel: String };

  connect() {
    window.__prospectMessage = this;

    this.dialog = this.element.querySelector('dialog');
    this.boundHandleClose = this.handleClose.bind(this);
    this.dialog?.addEventListener('close', this.boundHandleClose);
  }

  disconnect() {
    this.resolvePending(false); // settle any pending confirmMessage() so its caller doesn't hang forever
    if (window.__prospectMessage === this) {
      delete window.__prospectMessage;
    }
    this.dialog?.removeEventListener('close', this.boundHandleClose);
  }

  // internal - call showMessage(message) instead of this directly
  showNotice(message) {
    this.resolvePending(false); // supersede any pending confirm() - see handleClose

    this.cancelButtonTarget.classList.add('hidden');
    this.confirmButtonTarget.textContent = this.okLabelValue;
    this.messageTarget.innerHTML = message || '';

    this.open();
  }

  // internal - call confirmMessage(message) instead of this directly
  askConfirm(message) {
    this.resolvePending(false); // supersede any pending confirm() - see handleClose

    this.cancelButtonTarget.classList.remove('hidden');
    this.confirmButtonTarget.textContent = this.confirmLabelValue;
    this.messageTarget.innerHTML = message || '';

    this.open();

    return new Promise((resolve) => {
      this.resolveCurrent = resolve;
    });
  }

  open() {
    openModal(this.element.id);
  }

  // the single visible-in-both-modes button: closes a notice, or resolves a pending
  // confirm() true - harmless no-op for a notice, since nothing is awaiting it
  confirm() {
    this.resolvePending(true);
  }

  cancel() {
    this.resolvePending(false);
  }

  // catches every other way of dismissing the dialog - a backdrop click, or the
  // Escape key (which closes the native <dialog> directly, bypassing the modal
  // controller's own close() method). There is no header close button - this modal
  // renders with show_close_button: false for a minimal UI.
  handleClose() {
    this.resolvePending(false);
  }

  resolvePending(value) {
    this.resolveCurrent?.(value);
    this.resolveCurrent = null;
  }
}
